/**
 * Format phone number to (XXX) XXX-XXXX format
 */
export function formatPhoneNumber(value: string): string {
  // Remove all non-digits
  const cleaned = value.replace(/\D/g, '');

  // Format based on length
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 3) return `(${cleaned}`;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;

  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
}

/**
 * Format currency amount to $X,XXX.XX format
 */
export function formatCurrency(value: string | number): string {
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;

  if (isNaN(numValue)) return '';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(numValue);
}

/**
 * Format number with commas (for display only, not currency)
 */
export function formatNumber(value: string | number): string {
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;

  if (isNaN(numValue)) return '';

  return new Intl.NumberFormat('en-US').format(numValue);
}

/**
 * Parse formatted currency back to number
 */
export function parseCurrency(value: string): number {
  const cleaned = value.replace(/[^0-9.-]/g, '');
  return parseFloat(cleaned) || 0;
}

/**
 * Format and validate zip code for USA (5 or 9 digits) or Canada (A1A 1A1)
 */
export function formatZipCode(value: string): string {
  // Remove all non-alphanumeric characters
  const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');

  // Check if it's Canadian postal code format (starts with letter)
  if (/^[A-Z]/.test(cleaned)) {
    // Canadian: A1A1A1 → A1A 1A1
    if (cleaned.length <= 3) return cleaned;
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)}`;
  }

  // USA zip code: 12345 or 12345-6789
  if (cleaned.length <= 5) return cleaned;
  return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 9)}`;
}

/**
 * Validate zip code format
 */
export function isValidZipCode(value: string): boolean {
  const cleaned = value.replace(/[^A-Z0-9]/gi, '');

  // USA: 5 or 9 digits
  if (/^\d{5}(\d{4})?$/.test(cleaned)) return true;

  // Canada: A1A1A1 format
  if (/^[A-Z]\d[A-Z]\d[A-Z]\d$/i.test(cleaned)) return true;

  return false;
}
